import type {
	DbQueryStat,
	DbQueryTrendPoint,
	DbQueryVersion,
	HttpEndpointStat,
	HttpEndpointTrendPoint,
} from "@/lib/api";
import type { PinnedQuery } from "@/lib/performance-pins";

export const PERFORMANCE_PERIODS = [
	{ hours: 1, label: "直近 1 時間" },
	{ hours: 24, label: "直近 24 時間" },
	{ hours: 24 * 7, label: "直近 7 日" },
	{ hours: 24 * 30, label: "直近 30 日" },
	{ hours: 24 * 90, label: "直近 90 日" },
] as const;

/**
 * 推移グラフの見出し。バックエンドの PerformanceTrendWindow と同じく、24 時間より短い期間も 24 時間分見せ、
 * 7 日を超える期間は 1 日ごとにまとめる。
 */
export function trendCaption(hours: number): string {
	const shown = Math.max(hours, 24);
	const label = PERFORMANCE_PERIODS.find((p) => p.hours === shown)?.label ?? `直近 ${shown} 時間`;
	return `${label}の推移 (${shown > 24 * 7 ? "1 日" : "1 時間"}ごと)`;
}

/** グラフの縦軸の上限。最大値を 1・2・5 × 10^n に切り上げる。値が無ければ 1。 */
export function niceTrendMax(value: number): number {
	if (!(value > 0)) return 1;
	const base = 10 ** Math.floor(Math.log10(value));
	return ([1, 2, 5].find((step) => value <= step * base) ?? 10) * base;
}

const ENDPOINT_SORT_KEYS = {
	count: (e: HttpEndpointStat) => e.requestCount,
	errors: (e: HttpEndpointStat) => e.errorCount,
	mean: (e: HttpEndpointStat) => e.meanMs,
	p50: (e: HttpEndpointStat) => e.p50Ms,
	p95: (e: HttpEndpointStat) => e.p95Ms,
	p99: (e: HttpEndpointStat) => e.p99Ms,
	max: (e: HttpEndpointStat) => e.maxMs,
	total: (e: HttpEndpointStat) => e.totalMs,
};

export type EndpointSort = keyof typeof ENDPOINT_SORT_KEYS;

/** 上位を見る表なので、どの列も大きい順に並べる。 */
export function sortEndpoints(
	endpoints: HttpEndpointStat[],
	sort: EndpointSort,
): HttpEndpointStat[] {
	const key = ENDPOINT_SORT_KEYS[sort];
	return [...endpoints].sort((a, b) => key(b) - key(a));
}

/** ミリ秒を桁に合わせて ms / 秒 / 分 で見せる。 */
export function formatMs(ms: number): string {
	if (ms < 10) return `${ms.toFixed(1)} ms`;
	if (ms < 1_000) return `${Math.round(ms)} ms`;
	if (ms < 60_000) return `${(ms / 1_000).toFixed(ms < 10_000 ? 2 : 1)} 秒`;
	return `${(ms / 60_000).toFixed(1)} 分`;
}

/** 件数がこれより少ない期間は平均がぶれるため、前の期間と比べない。 */
const TREND_MIN_COUNT = 10;
/** 今の値がこれより小さければ、変わっても影響が小さいため比べない。 */
const TREND_MIN_MS = 100;
/** この割合を超えて変わったら、増えた (減った) とみなす。 */
const TREND_THRESHOLD = 0.1;

export interface Trend {
	direction: "up" | "down" | "steady";
	/** 前の期間からの変化の割合 (0.25 なら 25% 増えた)。 */
	change: number;
	/** 比べた前の期間の値。合計は今の期間と同じ長さに換算した値。 */
	previousMs: number;
}

export interface PeriodTotals {
	count: number;
	totalMs: number;
}

/** 今の期間 (since から observedAt まで) と、直前の期間 (previousSince から since まで)。 */
export interface Periods {
	observedAt: string;
	since: string | null;
	previousSince: string | null;
}

function compare(
	current: PeriodTotals,
	previous: PeriodTotals,
	currentMs: number,
	previousMs: number,
) {
	if (current.count < TREND_MIN_COUNT || previous.count < TREND_MIN_COUNT) return null;
	if (currentMs < TREND_MIN_MS || previousMs <= 0) return null;
	const change = currentMs / previousMs - 1;
	const direction: Trend["direction"] =
		change > TREND_THRESHOLD ? "up" : change < -TREND_THRESHOLD ? "down" : "steady";
	return { direction, change, previousMs };
}

/** 直前の同じ長さの期間と比べた平均の変化。比べられなければ null。 */
export function meanTrend(current: PeriodTotals, previous: PeriodTotals): Trend | null {
	return compare(
		current,
		previous,
		current.totalMs / current.count,
		previous.totalMs / previous.count,
	);
}

/**
 * 直前の期間と比べた合計の変化。今の期間は始点の正時の端数ぶん、DB の写しは 1 日 1 回分しか残らない分だけ
 * 長さがずれるため、前の期間の合計を今の期間と同じ長さに換算して比べる。比べられなければ null。
 */
export function totalTrend(
	current: PeriodTotals,
	previous: PeriodTotals,
	periods: Periods,
): Trend | null {
	if (periods.since === null || periods.previousSince === null) return null;
	const since = Date.parse(periods.since);
	const scale =
		(Date.parse(periods.observedAt) - since) / (since - Date.parse(periods.previousSince));
	return compare(current, previous, current.totalMs, previous.totalMs * scale);
}

export interface RankedQuery {
	query: DbQueryStat;
	/** 上位 limit 件の中の順位。上位から外れたピン留めのクエリは null。 */
	rank: number | null;
}

/** タグの付いたピン留めは、書き換えてクエリ ID が変わっても同じタグのクエリに当てはまる。 */
export function matchesPin(
	query: Pick<DbQueryStat, "queryId" | "tag">,
	pin: Pick<PinnedQuery, "queryId" | "tag">,
): boolean {
	return query.queryId === pin.queryId || (pin.tag !== null && query.tag === pin.tag);
}

/**
 * サーバーは上位 limit 件の後ろに、上位から外れたピン留めのクエリを続けて返す。ピン留めを先に分け、
 * ピン留めを外した直後の読み直し前でも、上位から外れた行は残さない。
 */
export function splitPinnedQueries(
	queries: DbQueryStat[],
	limit: number,
	pins: PinnedQuery[],
): { pinned: RankedQuery[]; others: RankedQuery[] } {
	const ranked = queries.map((query, i) => ({ query, rank: i < limit ? i + 1 : null }));
	const isPinned = (q: DbQueryStat) => pins.some((p) => matchesPin(q, p));
	return {
		pinned: ranked.filter((r) => isPinned(r.query)),
		others: ranked.filter((r) => r.rank !== null && !isPinned(r.query)),
	};
}

function previousMeanMs(count: number, totalMs: number): number | null {
	return count === 0 ? null : totalMs / count;
}

/** 1 回あたりの平均行数。小さい値は小数 1 桁まで見せる。 */
export function formatMeanRows(rows: number): string {
	return rows < 10 ? rows.toFixed(1) : Math.round(rows).toLocaleString();
}

type CsvCell = string | number | null;

/** 表計算ソフトに貼れる CSV にする。区切り・引用符・改行を含むセルだけ引用符で囲む。 */
export function toCsv(rows: CsvCell[][]): string {
	const cell = (value: CsvCell) => {
		const text = value === null ? "" : String(value);
		return /[",\r\n]/.test(text) ? `"${text.replaceAll('"', '""')}"` : text;
	};
	return rows.map((row) => row.map(cell).join(",")).join("\n");
}

/** クエリの改行は文字の \n にして、1 クエリを表計算ソフトの 1 行に収める。 */
export function dbQueriesCsv(queries: DbQueryStat[]): string {
	return toCsv([
		[
			"クエリ",
			"実行回数",
			"平均 (ms)",
			"前の期間の平均 (ms)",
			"標準偏差 (ms)",
			"最大 (ms)",
			"合計時間 (ms)",
			"平均行数",
			"キャッシュ率",
		],
		...queries.map((q) => [
			q.query.replace(/\r\n|\r|\n/g, "\\n"),
			q.calls,
			q.meanMs,
			previousMeanMs(q.previousCalls, q.previousTotalMs),
			q.stddevMs,
			q.maxMs,
			q.totalMs,
			q.meanRows,
			q.cacheHitRatio,
		]),
	]);
}

export function endpointsCsv(endpoints: HttpEndpointStat[]): string {
	return toCsv([
		[
			"メソッド",
			"ルート",
			"リクエスト数",
			"5xx",
			"平均 (ms)",
			"前の期間の平均 (ms)",
			"p50 (ms)",
			"p95 (ms)",
			"p99 (ms)",
			"最大 (ms)",
			"合計時間 (ms)",
		],
		...endpoints.map((e) => [
			e.method,
			e.route,
			e.requestCount,
			e.errorCount,
			e.meanMs,
			previousMeanMs(e.previousRequestCount, e.previousTotalMs),
			e.p50Ms,
			e.p95Ms,
			e.p99Ms,
			e.maxMs,
			e.totalMs,
		]),
	]);
}

export function dbQueryTrendCsv(points: DbQueryTrendPoint[]): string {
	return toCsv([
		["開始", "終了", "実行回数", "平均 (ms)", "クエリ ID"],
		...points.map((p) => [p.start, p.end, p.calls, p.meanMs, p.queryId]),
	]);
}

/**
 * 点ごとに、その時点で使われていたクエリ ID。実行されなかった区間は直前のものを引き継ぎ、
 * 最初に実行されるより前は null。
 */
export function queryIdsInEffect(points: DbQueryTrendPoint[]): (string | null)[] {
	const ids: (string | null)[] = [];
	let current: string | null = null;
	for (const p of points) {
		current = p.queryId ?? current;
		ids.push(current);
	}
	return ids;
}

export interface QueryVersionSpan extends DbQueryVersion {
	/** 古い順に「版 1」「版 2」…。 */
	label: string;
	/** 最も多く実行されていた最初の区間の始まりと、最後の区間の終わり。 */
	from: string | null;
	to: string | null;
}

export function queryVersionSpans(
	points: DbQueryTrendPoint[],
	versions: DbQueryVersion[],
): QueryVersionSpan[] {
	return versions.map((v, i) => {
		const own = points.filter((p) => p.queryId === v.queryId);
		return {
			...v,
			label: `版 ${i + 1}`,
			from: own.at(0)?.start ?? null,
			to: own.at(-1)?.end ?? null,
		};
	});
}

export function endpointTrendCsv(points: HttpEndpointTrendPoint[]): string {
	return toCsv([
		["開始", "終了", "リクエスト数", "5xx", "平均 (ms)", "p95 (ms)"],
		...points.map((p) => [p.start, p.end, p.requestCount, p.errorCount, p.meanMs, p.p95Ms]),
	]);
}
